import { useState, useEffect, useMemo, Fragment, useRef  } from 'react'
import { t } from 'i18next';
import { useRecoilValue } from 'recoil';
import {useDropzone} from 'react-dropzone'
import { Col, Label } from 'reactstrap'
import axios from 'axios';
import moment from 'moment';

import env from '../../env/src_config';
import { headersState, tokenState } from '../../recoil/recoil';
import { axiosError, errorStatus } from '../../helpers/response';
import { isNull, bytesToSize } from '../../izUtils';
import { getFilePreview } from './getFilePreview';
import DictionaryInfoButton from '../dictionary/DictionaryInfoButton';
import Loader from '../spinner/Loader';

const FileUpload = ({data, saveToLS, apiUrl, noDelete, inputType, taskData, showErrors, dictionaryKey}) => {
    const headers = useRecoilValue(headersState);
    const token = useRecoilValue(tokenState);
    const [files, setFiles] = useState([])
    const [loadingAdd, setLoadingAdd] = useState(false)
    const [loadingDelete, setLoadingDelete] = useState(null)

    const cameraInputRef = useRef(null);

    useEffect(() => {
        if (!isNull(data.values) && data.values.length !== 0) {
            setLoadingAdd(true);
            Promise.all(
                data.values.map(async (file) => {
                    if (!isNull(file.title)) {
                        const splited = file.title.split('.')
                        file.ext = splited[splited.length - 1]
                        if (['png', 'jpg', 'jpeg'].indexOf(file.ext) !== -1) {
                            const getFile = await getFilePreview(file, headers, apiUrl);
                            file.preview =  URL.createObjectURL(getFile)
                        }
                        return file;
                    } else {
                        return {id: file};
                    }
                })
            ).then(data => {
                setFiles(data)
                localStorage.setItem(saveToLS, JSON.stringify(data));
                setLoadingAdd(false);
            });
        }

        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        let url = '/api/public/file/';
        if (!isNull(apiUrl)) {
            url = apiUrl;
        } else if (!isNull(headers.Authorization) && headers.Authorization !== '') {
            url = '/api/private/file/';
        }

        try {
            const response = await axios.post(env.api + url + 'create', formData, { headers });
            if (!isNull(response.data.id)) {
                let lsFiles = JSON.parse(localStorage.getItem(saveToLS));
                let responseData = { ...response.data };
                responseData.type = response.data.mime;
                responseData.preview = URL.createObjectURL(file);

                if (isNull(lsFiles)) {
                    lsFiles = [responseData];
                } else {
                    lsFiles.push(responseData);
                }

                setFiles(lsFiles);
                localStorage.setItem(saveToLS, JSON.stringify(lsFiles));
            } else {
                errorStatus(response.data, t);
            }
        } catch (error) {
            axiosError(error);
        }
    }

    const processAndUploadFiles = async (acceptedFiles) => {
        setLoadingAdd(true);

        await Promise.all(
            acceptedFiles.map(file => {
                if (inputType === 'createInventoryReceipt') {
                    const originalFile = file;
                    const { name, type, lastModified } = originalFile;

                    const splitedName = name.split('.');
                    const fileExt = splitedName[splitedName.length - 1];

                    let firstLettersName = "";
                    token?.parsedToken?.name?.split(' ').forEach(name => firstLettersName += name[0]);

                    let facilityName = '';
                    taskData?.data.forEach(input => {
                        if (input.name === "facility") {
                            input.values.forEach(val => {
                                if (val.id === input.value) {
                                    facilityName = val.title;
                                }
                            });
                        }
                    });

                    const newName = firstLettersName + '-' + facilityName + '-' + moment().format('DDMMYYYY') + '.' + fileExt;
                    const renamedFile = new File([file], newName, { type, lastModified });
                    return uploadFile(renamedFile);
                } else {
                    return uploadFile(file);
                }
            })
        );

        setLoadingAdd(false);
    };

    const { getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        onDrop: processAndUploadFiles,
    });

    const handleFile = (file, type) => {
        if (type === 'get') {
            let url = '/api/public/file/';
            if (!isNull(apiUrl)) {
                url = apiUrl;
            } else if (!isNull(headers.Authorization) && headers.Authorization !== '') {
                url = '/api/private/file/';
            }

            axios.get(env.api + url + file.id, {headers, responseType: 'blob'}).then(response => {
                const href = window.URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.download = file.title;
                a.href = href;
                a.click();
                a.href = '';
            }).catch(error => {
                axiosError(error);
            });
        } else if (type === 'delete') {
            if (!data.disabled) {
                let url = '/api/private/file/';
                if (inputType === 'createInventoryReceipt' || inputType === 'singleInventoryReceipt' || inputType === 'createService' || inputType === 'singleService') {
                    url = apiUrl;
                }

                setLoadingDelete(file.id);
                axios.delete(env.api + url + file.id, {headers}).then(response => { // Delete is only on private route
                    if (!isNull(response.data.state) && response.data.state === "success") {
                        let lsFiles = JSON.parse(localStorage.getItem(saveToLS));
                        if (!isNull(lsFiles)) lsFiles = lsFiles.filter(f => f.id !== file.id )
                        setFiles(lsFiles)
                        localStorage.setItem(saveToLS, JSON.stringify(lsFiles));
                    } else {
                        errorStatus(response.data, t);
                    }
                    setLoadingDelete(null);
                }).catch(error => {
                    setLoadingDelete(null);
                    axiosError(error);
                });
            }
        }
    }

    const displayFiles = useMemo(() => {
        return files.map(file => (
            <li key={file.id} className="file-box">
                <div className="file-top" onClick={() => handleFile(file, 'get')}>
                    {['png', 'jpg', 'jpeg'].indexOf(file.ext) !== -1 ?
                        <img src={file.preview}
                            alt={file.title}
                            className="task-image"
                            onLoad={() => { URL.revokeObjectURL(file.preview) }} // Revoke data uri after image is loaded
                        />
                        :
                        <i className={`fa fa-file-${file.ext}-o txt-primary`}></i>
                    }
                </div>
                <div className="file-bottom">
                    <h6 onClick={() => handleFile(file, 'get')}>{file.title}</h6>
                    <div className='d-flex flex-wrap justify-content-between'>
                        <div className="mb-0 mt-1" style={{ opacity: '0.9' }}>{bytesToSize(file.size)}</div>
                        {(!data.disabled && !noDelete) && <i className= "icon-trash bigger-icon" onClick={() => handleFile(file, 'delete')}></i>}
                    </div>
                </div>
                {(!isNull(loadingDelete) && (loadingDelete === file.id)) &&
                    <div style={{ position: 'absolute', inset: '0', backgroundColor: '#00000030' }}>
                        <span className="loader-box">
                            <div className="loader-19"></div>
                        </span>
                    </div>
                }
            </li>
        ));
    }, [files, data.disabled, loadingDelete]) // eslint-disable-line react-hooks/exhaustive-deps

    const baseStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '50px',
        borderWidth: 2,
        borderRadius: 2,
        borderColor: 'var(--theme-default)',
        borderStyle: 'dashed',
        backgroundColor: '#00aeef20',
        color: '#000',
        outline: 'none',
        transition: 'border .24s ease-in-out',
        marginBottom: '10px',
      };

    const style = {...baseStyle}

    if (data.display) {
        return (
            <Fragment>
                {(!data.disabled || (data.disabled && displayFiles.length !== 0)) &&
                    <div className='d-flex align-items-center'>
                        <Label>{data.title + (data.mandatory ? " *" : "")}</Label>
                        {dictionaryKey && <DictionaryInfoButton code={dictionaryKey} id={dictionaryKey} marginBottom="0.2rem" />}
                    </div>
                }
                <div className='file-content'>
                    <div className='file-manager '>
                        <div className='files'>
                            {!data.disabled &&
                                <>
                                    {/* Hidden dropzone input */}
                                    <input {...getInputProps()} />

                                    {/* Hidden camera input */}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        ref={cameraInputRef}
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                processAndUploadFiles(Array.from(e.target.files));
                                            }
                                        }}
                                    />

                                    <div {...getRootProps({ style })}>
                                        <p className='text-center mb-3'>{t('dragndrop')}</p>
                                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={open}
                                            >
                                                {t('uploadFile')}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={() => cameraInputRef.current?.click()}
                                            >
                                                {t('takePhoto')}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            }
                            <Col sm="12">
                                <div className={'d-flex flex-wrap mb-3' + (displayFiles?.length !== 0 ? ' attachments-container' : '')}>
                                    {displayFiles}
                                    {loadingAdd && <Loader position="fixed" />}
                                </div>
                            </Col>
                        </div>
                    </div>
                </div>
            </Fragment>
        )
    } else {
        return null;
    }

}

export default FileUpload